import React, { FC, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import { Dispatch } from 'redux';
import { COURSE_CREATED } from 'redux/actions';
import { API, request } from 'redux/api';
import { RootState } from 'redux/reducers';
import { ROUTES } from 'routes';

import { ReactComponent as StarIcon } from 'assets/star-blue.svg';

import { Button, CheckBox } from '../UtilElements';

const advantages = [
  {
    label: 'Sync roster updates at the click of a button',
    icon: '',
  },
  {
    label: "Get actionable insights on students' learning behavior",
    icon: '',
  },
  {
    label: 'Export quiz, polls, assignments, attendance, participation scores, etc. directly into your LMS',
    icon: '',
  },
  {
    label: 'Compare your teaching strategies and their impact over the week',
    icon: '',
  },
];

interface CreateCoursePayload {
  courseId: string;
  notifyStudents: NumericBoolean;
  notifyMembers: NumericBoolean;
  enableAutoSync: NumericBoolean;
}

const createCourse = (data: CreateCoursePayload) => {
  return request<MessageResponse>({
    method: 'post',
    url: API.COURSE_CREATE_COMPLETE,
    data,
  });
};

interface CourseConfirmProps {
  onPrev: () => void;
  course: CourseDetails | null;
  accessData: SessionData | null;
  selectedChildCourseId: string | null;
  courseCreated: (courseId: string, childCourseId: string | null) => void;
}

const CourseConfirm: FC<CourseConfirmProps> = ({
  onPrev,
  course,
  accessData,
  selectedChildCourseId,
  courseCreated,
}) => {
  const [notifyStudents, setNotifyStudents] = useState<NumericBoolean>(1);
  const [notifyMembers, setNotifyMembers] = useState<NumericBoolean>(1);
  const [enableAutoSync, setEnableAutoSync] = useState<NumericBoolean>(1);

  const handleGoBack = useCallback(() => {
    onPrev();
  }, [onPrev]);

  return (
    <>
      <div className='import-course__form with-actions'>
        <label className='import-course__label'>Advantages of a Linked Course</label>
        <ul className={`import-course__advantage-list`}>
          {advantages.map((ad) => (
            <li key={ad.label} className='import-course__advantage-item'>
              <StarIcon />
              <p>{ad.label}</p>
            </li>
          ))}
        </ul>
      </div>
      <section className='import-course__info'>
        <h2 className='import-course__title'>Create A Linked Course - Confirmation</h2>
        <p className='import-course__description'>
          Now that course details have been imported, all you need to do is give us a final nod
        </p>
        <div className='import-course__description'>
          <CheckBox
            isChecked={notifyStudents === 1}
            className='import-course__checkbox'
            onToggle={() => setNotifyStudents((notifyStudents) => (notifyStudents === 1 ? 0 : 1))}
          >
            Notify students now
          </CheckBox>
          <CSSTransition in={notifyStudents === 0} classNames='fade' timeout={300} unmountOnExit>
            <p className='import-course__checkbox-note'>
              Students will be notified the next time you sync the enrollments
            </p>
          </CSSTransition>
          <CheckBox
            isChecked={notifyMembers === 1}
            className='import-course__checkbox'
            onToggle={() => setNotifyMembers((notifyMembers) => (notifyMembers === 1 ? 0 : 1))}
          >
            Notify co-instructors, teaching assistants now
          </CheckBox>
          <CSSTransition in={notifyMembers === 0} classNames='fade' timeout={300} unmountOnExit>
            <p className='import-course__checkbox-note'>
              Members will be notified the next time you sync the enrollments
            </p>
          </CSSTransition>
          <CheckBox
            isChecked={enableAutoSync === 1}
            className='import-course__checkbox'
            onToggle={() => setEnableAutoSync((enableAutoSync) => (enableAutoSync === 1 ? 0 : 1))}
          >
            Enable auto sync
          </CheckBox>
          <CSSTransition in={enableAutoSync === 0} classNames='fade' timeout={300} unmountOnExit>
            <p className='import-course__checkbox-note'>Course enrollments will have to be synced manually</p>
          </CSSTransition>
        </div>
        <div className='import-course__actions'>
          <div className='import-course__action'>
            <Button
              preventMinWidth
              className='import-course__action__back'
              variant='text'
              color='info'
              size='large'
              onClick={handleGoBack}
            >
              Back
            </Button>
          </div>
          <div className='import-course__action'>
            <Link
              to={{
                pathname:
                  accessData?.context === 'course' ? ROUTES.COURSE_MEMBERS : ROUTES.ADMIN_COURSES_SELECT,
                state: { courseId: accessData?.context === 'course' ? undefined : course?.courseId },
              }}
              component={({ navigate, href }) => {
                return (
                  <Button
                    variant='contained'
                    color='success'
                    size='large'
                    onClick={async () => {
                      if (!course?.courseId) return;
                      await createCourse({
                        courseId: course.courseId,
                        notifyStudents,
                        notifyMembers,
                        enableAutoSync,
                      });
                      courseCreated(course.courseId, selectedChildCourseId);
                      navigate(href);
                    }}
                  >
                    Create Course
                  </Button>
                );
              }}
            />
          </div>
        </div>
      </section>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  accessData: state.getAccess,
  course: state.courseInCreation,
  selectedChildCourseId: state.selectedChildCourseId,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  courseCreated: (courseId: string, childCourseId: string | null) => {
    dispatch({
      type: COURSE_CREATED,
      payload: { courseId, childCourseId },
    });
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CourseConfirm);
